import { useServiceReviews, useReviewStats } from "@/hooks/useServiceReviews";
import { useProviderImpression } from "@/hooks/useProviderImpression";
import { useProviderContact } from "@/hooks/useProviderContact";
import { useProviderSubscription } from "@/hooks/useProviderSubscription";
import { useContactInfo } from "@/hooks/useContactInfo";
import { isProviderVerified } from "@/utils/verificationHelpers";
import { useAreaDisplay } from "@/hooks/useAreaDisplay";
import ServiceCardHeader from "./service-card/ServiceCardHeader";
import ServiceCardContent from "./service-card/ServiceCardContent";
import ServiceCardActions from "./service-card/ServiceCardActions";

interface ServiceCardProps {
  id: string;
  name: string;
  image: string;
  description: string;
  rating: number;
  user_id: string;
  isVerified: boolean;
  isFeatured?: boolean;
  categories?: string[];
  suburbs?: string[];
  websiteUrl?: string;
  pageType?: 'explore' | 'category' | 'search' | 'home' | 'favorites';
  searchQuery?: string;
  categoryFilter?: string;
  userProfileVerified?: boolean;
  onProviderClick?: () => void;
  onWhatsAppClick?: () => void;
}

const ServiceCard = ({
  id,
  name,
  image,
  description,
  user_id,
  isVerified,
  isFeatured = false,
  categories = [],
  suburbs = [],
  pageType = 'explore',
  searchQuery,
  categoryFilter,
  userProfileVerified,
  onProviderClick,
  onWhatsAppClick,
  whatsapp_number,
}: ServiceCardProps & {
  whatsapp_number?: string | null;
}) => {
  const { data: reviews, isLoading: isLoadingReviews } = useServiceReviews(id);
  const { averageRating, totalReviews, isTopRated } = useReviewStats(reviews);
  
  const impressionRef = useProviderImpression({
    providerId: id,
    pageType,
    searchQuery,
    category: categoryFilter,
  });
  const { trackContact } = useProviderContact(id);

  const { contactInfo } = useContactInfo({ userId: user_id, listingWhatsApp: whatsapp_number });
  const { isPremiumProvider } = useProviderSubscription(user_id, id);
  const { areaLabels } = useAreaDisplay(suburbs);

  const hasReviews = Boolean(!isLoadingReviews && reviews && reviews.length > 0);
  
  const showVerifiedBadge = Boolean(isVerified);

  return (
  <div ref={impressionRef} className="relative flex h-full min-w-0 w-full flex-col overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <ServiceCardHeader
        id={id}
        name={name}
        image={image}
        isFeatured={isFeatured}
        isTopRated={isTopRated}
        whatsappVerified={showVerifiedBadge}
          onProviderClick={onProviderClick}
      />
      
      <div className="p-5 flex flex-col flex-grow">
        <ServiceCardContent
          id={id}
          name={name}
          hasReviews={hasReviews}
          averageRating={averageRating}
          totalReviews={totalReviews}
          categories={categories}
          areaLabels={areaLabels}
          description={description}
          onProviderClick={onProviderClick}
        />
        
        <ServiceCardActions
          isPremiumProvider={isPremiumProvider}
          serviceName={name}
          userContactInfo={contactInfo}
          trackContact={trackContact}
          onWhatsAppClick={onWhatsAppClick}
        />
      </div>
    </div>
  );
};

export default ServiceCard;
